import { Dialog } from "@mui/material";
import api from "config/api";
import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import noImage from "assets/images/no-image.png";
import { Edit } from "@mui/icons-material";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import Input from "components/common/Input";
import Button from "components/common/Button";

type Props = {
  selected?: any;
  setSelected: (v?: any) => void;
  onComplete: (v?: boolean) => void;
};

const MAX_FILE_SIZE = 2 * 1024 * 1024;

const EditDepartmentDialog = ({ setSelected, selected, onComplete }: Props) => {
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState<File | undefined>(undefined);

  const imageUrl = useMemo(() => {
    if (image) {
      return URL.createObjectURL(image);
    }
    if (!selected?.imageUrl) return noImage;
    return selected.imageUrl;
  }, [image, selected]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files ? e.target.files[0] : undefined;
    if (file && file.size > MAX_FILE_SIZE) {
      toast.error("File size must not exceed 2 MB.");
      return;
    }
    setImage(file);
  };

  const handelDeleteFile = () => {
    setLoading(true);
    api
      .post(`/api/FileUtility/RemoveDeptUploadFile?deptid=${selected!.id}`)
      .then((e) => {
        setImage(undefined);
        onComplete(true);
        setSelected(undefined);
        toast.success("Image deleted successfully");
      })
      .catch(() => {
        toast.error("Something went wrong while deleting image");
      })
      .finally(() => setLoading(false));
  };

  const handleSubmit = async () => {
    try {
      if (!image) {
        toast.error("Please upload an image");
        return;
      }
      const formData = new FormData();
      formData.append("formFile", image);

      const response = await api.post(
        `/api/FileUtility/UploadDeptFiles?deptid=${selected!.id}`,
        formData
      );

      const responseData = response.data?.data[0];
      setImage(undefined);
      if (responseData?.status === "ERROR") {
        toast.error(responseData.errorMessage || "Failed to upload the file.");
        return;
      }
      setSelected(undefined);
      toast.success("Department updated successfully");
      onComplete();
    } catch (e: any) {
      toast.error(e);
    }
  };

  return (
    <Dialog onClose={() => setSelected()} open={!!selected}>
      <div className="min-w-[80vw] md:min-w-[500px]  overflow-auto  py-2.5 flex flex-col">
        <h4 className="!text-lg text-primary font-semibold text-center border-b pb-2">
          Update Department
        </h4>
        <div className="border-b px-4 py-0.5 text-[15px] flex-1 flex flex-col gap-4 pt-3">
          <div className="size-[150px] aspect-square self-center border rounded-full relative">
            <img
              src={imageUrl}
              alt="Department "
              className="w-full h-full rounded-full object-cover"
              onError={(e: any) => {
                e.currentTarget.src = noImage;
              }}
            />
            <input
              id="image-input"
              type="file"
              onChange={handleChange}
              className="hidden"
            />
            <div
              className="size-7 flex justify-center items-center absolute bottom-[5%] right-[5%] rounded-full bg-primary  cursor-pointer"
              onClick={() => {
                const input = document.getElementById("image-input")!;
                input.click();
              }}
            >
              <Edit className="!text-[15px] text-white" />
            </div>
            <div
              className="size-7 flex justify-center items-center absolute bottom-[5%] left-[5%] rounded-full bg-primary  cursor-pointer"
              onClick={handelDeleteFile}
            >
              <DeleteRoundedIcon className="!text-[15px] text-white" />
            </div>
          </div>

          <Input
            disabled
            size="small"
            className="!mb-3"
            label="Name"
            value={selected?.categoryDescription}
          />
        </div>
        <Button
          className="mt-3 mx-3 "
          disabled={loading}
          onClick={handleSubmit}
        >
          Update
        </Button>
      </div>
    </Dialog>
  );
};

export default EditDepartmentDialog;
